/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { isArray } from 'lodash'
import { Plus } from 'phosphor-react'
import { speaker } from 'constants/Events'
import { getUsers } from 'containers/users/Api'
import { toast } from 'react-toastify'
import CustomToast from 'components/common/custom-toast/CustomToast'
import { alertTypes } from 'constants/Common'
import SpeakerAccordionCard from './SpeakerAccordionCard'
import SpeakerModal from './SpeakerModal'

function speakerUserId(user) {
  if (!user) return ''
  if (user.id !== undefined && user.id !== null && user.id !== '') {
    return String(user.id)
  }
  // eslint-disable-next-line no-underscore-dangle
  return user._id ? String(user._id) : ''
}

function speakerDisplayName(user) {
  if (!user) return ''
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return user.name || fullName || user.email || ''
}

function mapUsersToSpeakerOptions(rows) {
  return rows
    .filter((user) => user?.type === 'speaker')
    .map((user) => {
      const id = speakerUserId(user)
      const name = speakerDisplayName(user)
      if (!id || !name) return null
      return {
        value: id,
        label: name,
        speaker: {
          name,
          // eslint-disable-next-line no-underscore-dangle
          userId: id,
          designation: user.designation || user.jobTitle || '',
          description: user.description || '',
          image: user.image || '',
          mobileNumber: String(user.phone || '').replace(/^\+966/, ''),
          email: user.email || '',
        },
      }
    })
    .filter(Boolean)
}

function mergeSpeakerOption(options, userLike) {
  if (!userLike) return options
  const [option] = mapUsersToSpeakerOptions([
    { ...userLike, type: userLike.type || 'speaker' },
  ])
  if (!option) return options
  if (options.some((item) => item.value === option.value)) {
    return options
  }
  return [option, ...options]
}

function SpeakerStep() {
  const formik = useFormikContext()
  const [openSpeakerIndex, setOpenSpeakerIndex] = useState(0)
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [speakerOptions, setSpeakerOptions] = useState([])
  const [speakersLoading, setSpeakersLoading] = useState(true)
  const lastSubmitCountRef = useRef(0)

  const loadSpeakerOptions = useCallback(async (createdSpeakerUser) => {
    if (createdSpeakerUser) {
      setSpeakerOptions((previous) =>
        mergeSpeakerOption(previous, createdSpeakerUser)
      )
    }

    setSpeakersLoading(true)
    try {
      const result = await getUsers(1, 500, '', '')
      const rows = Array.isArray(result?.data) ? result.data : []
      let nextOptions = mapUsersToSpeakerOptions(rows)
      if (createdSpeakerUser) {
        nextOptions = mergeSpeakerOption(nextOptions, createdSpeakerUser)
      }
      setSpeakerOptions(nextOptions)
    } finally {
      setSpeakersLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setSpeakersLoading(true)
      const result = await getUsers(1, 500, '', '')
      if (cancelled) return
      const rows = Array.isArray(result?.data) ? result.data : []
      setSpeakerOptions(mapUsersToSpeakerOptions(rows))
      setSpeakersLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const hasNewSubmitAttempt = formik.submitCount > lastSubmitCountRef.current
    if (hasNewSubmitAttempt && formik?.errors?.speakers) {
      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message='Please fill all the fields'
        />
      )
    }
    lastSubmitCountRef.current = formik.submitCount
  }, [formik.submitCount, formik.errors?.speakers])

  const handleAddEmptySpeaker = () => {
    const nextList = [
      ...(formik.values.speakers || []),
      { ...speaker, mobileNumber: '' },
    ]
    formik.setFieldValue('speakers', nextList)
    setOpenSpeakerIndex(nextList.length - 1)
  }

  const handleSpeakerSelect = (index, selectedOption) => {
    const next = [...(formik.values.speakers || [])]
    const selectedSpeaker = selectedOption?.speaker || {}
    next[index] = {
      ...next[index],
      ...selectedSpeaker,
      name: selectedSpeaker.name || selectedOption?.label || '',
    }
    formik.setFieldValue('speakers', next)
  }

  const handleDeleteSpeaker = (index) => {
    const next = [...(formik.values.speakers || [])]
    next.splice(index, 1)
    formik.setFieldValue('speakers', next)
    if (!next.length) {
      setOpenSpeakerIndex(0)
      return
    }
    setOpenSpeakerIndex((prev) => Math.min(prev, next.length - 1))
  }

  const selectedSpeakerIds = useMemo(
    () =>
      (formik.values.speakers || [])
        .map((item) => item?.userId || '')
        .filter(Boolean),
    [formik.values.speakers]
  )

  const getAvailableSpeakerOptions = (currentIndex) => {
    const currentSpeakerId =
      formik.values.speakers?.[currentIndex]?.userId || ''
    return speakerOptions.filter((option) => {
      if (option.value === currentSpeakerId) return true
      return !selectedSpeakerIds.includes(option.value)
    })
  }

  return (
    <div className='event-create-card'>
      <div className='event-speaker-header'>
        <p className='heading-lg mb-0'>Speaker</p>
        <button
          type='button'
          className='event-speaker-header__add'
          onClick={() => setIsCreateModalVisible(true)}
        >
          <Plus size={16} weight='bold' />
          <span>Add New Speaker</span>
        </button>
      </div>
      <div className='event-speaker-stack'>
        {(formik.values.speakers || []).map((item, index) => (
          <SpeakerAccordionCard
            key={`speaker-${index}`}
            title={`Speaker-${index + 1}`}
            isOpen={openSpeakerIndex === index}
            onToggle={() =>
              setOpenSpeakerIndex((prev) => (prev === index ? -1 : index))
            }
            speaker={item}
            speakerOptions={getAvailableSpeakerOptions(index)}
            speakersLoading={speakersLoading}
            errors={formik?.errors?.speakers?.[index]}
            submitCount={formik.submitCount}
            onSelectSpeaker={(option) => handleSpeakerSelect(index, option)}
            onDelete={() => handleDeleteSpeaker(index)}
          />
        ))}
      </div>
      <button
        type='button'
        className='event-speaker-add-btn'
        onClick={handleAddEmptySpeaker}
      >
        <Plus size={18} />
        <span>Add Speakers</span>
      </button>
      <p className='error-text'>
        {!isArray(formik?.errors?.speakers) && formik?.errors?.speakers}
      </p>

      {isCreateModalVisible ? (
        <SpeakerModal
          handleCloseModal={() => setIsCreateModalVisible(false)}
          formik={formik}
          openSpeakerIndex={openSpeakerIndex}
          onSpeakerCreated={loadSpeakerOptions}
        />
      ) : null}
    </div>
  )
}

export default SpeakerStep
